import { ref, watch, type Ref } from 'vue'
import { useRoute, useRouter, type LocationQueryValue } from 'vue-router'

function isEmpty(v: unknown): boolean {
    return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)
}

function debounce<T extends (...args: never[]) => void>(fn: T, ms: number): T {
    let timer: number | undefined
    return ((...args: never[]) => {
        if (timer) window.clearTimeout(timer)
        timer = window.setTimeout(() => fn(...args), ms)
    }) as T
}

export type FilterParser<V> = (raw: LocationQueryValue | LocationQueryValue[] | undefined) => V

export interface UseFiltersOptions<T> {
    debounceMs?: number
    parsers?: Partial<{ [K in keyof T]: FilterParser<T[K]> }>
}

export function useFilters<T extends object>(initial: T, options: UseFiltersOptions<T> = {}) {
    const route = useRoute()
    const router = useRouter()

    const filters: Ref<T> = ref({ ...initial }) as Ref<T>

    // load from URL on init
    function loadFromQuery() {
        const next = { ...initial } as T
        for (const key of Object.keys(initial) as (keyof T)[]) {
            const raw = route.query[key as string] as LocationQueryValue | LocationQueryValue[] | undefined
            if (raw === undefined || raw === null) continue
            const parser = options.parsers?.[key]
            if (parser) {
                next[key] = parser(raw)
            } else {
                next[key] = (Array.isArray(raw) ? raw[0] : raw) as T[keyof T]
            }
        }
        filters.value = next
    }
    loadFromQuery()

    const syncToQuery = debounce(() => {
        const q: Record<string, string> = {}
        for (const [k, v] of Object.entries(filters.value as Record<string, unknown>)) {
            if (!isEmpty(v)) q[k] = String(v)
        }
        router.replace({ query: q })
    }, options.debounceMs ?? 300)

    watch(filters, syncToQuery, { deep: true })

    function reset() {
        filters.value = { ...initial }
    }

    return { filters, reset }
}
